'use no memo'

import { mailingListRecipientFormSchema, type MailingListRecipientFormValues } from '@gozoki/api'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useParams } from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { AuthorizedAppButton } from '~/components/new/buttons/authorized-button'
import { AuthorizedDeleteConfirmButton } from '~/components/new/buttons/delete-confirm-button'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { AppTable, AppTableData } from '~/components/new/app-ui/table/app-table'
import { Title } from '~/components/new/app-ui/title'
import { RbacAccessDenied } from '~/components/routes/administration/roles/access-denied'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { Switch } from '~/components/ui/switch'
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip'
import { useAppTable } from '~/hooks/use-app-table'
import type { MailingListRecipient } from '~/lib/mailing-lists-query'
import { api } from '~/lib/tuyau'
import type { PermissionTuple } from '~/utils/ability'
import { usePermissionGuard, withRoutePermissions } from '~/utils/rbac'

const READ: PermissionTuple = ['read', 'mailing-lists.mailing-lists.mailing-list']
const ADD: PermissionTuple = ['add', 'mailing-lists.mailing-lists.mailing-list']
const EDIT: PermissionTuple = ['edit', 'mailing-lists.mailing-lists.mailing-list']
const DELETE: PermissionTuple = ['delete', 'mailing-lists.mailing-lists.mailing-list']

export const Route = createFileRoute('/_authenticated/mailing-lists/$key')({
  staticData: {
    permission: READ,
  },
  component: MailingListDetailPage,
})
withRoutePermissions(Route)

const columnHelper = createColumnHelper<MailingListRecipient>()

function MailingListDetailPage() {
  const { key } = useParams({ from: '/_authenticated/mailing-lists/$key' })
  const canRead = usePermissionGuard(READ).authorized
  const editGuard = usePermissionGuard(EDIT)

  const { data: mailingList, isLoading } = useQuery(
    api.mailingLists.show.queryOptions({ params: { key } }),
  )

  const invalidatesMailingLists = [
    api.mailingLists.index.queryKey(),
    api.mailingLists.show.queryKey(),
  ]

  const addRecipient = useMutation(
    api.mailingLists.addRecipient.mutationOptions({
      meta: {
        invalidates: invalidatesMailingLists,
        errorMessage: "Impossible d'ajouter le destinataire.",
      },
    }),
  )
  const updateRecipient = useMutation(
    api.mailingLists.updateRecipient.mutationOptions({
      meta: {
        invalidates: invalidatesMailingLists,
        errorMessage: 'Impossible de modifier le destinataire.',
      },
    }),
  )
  const destroyRecipient = useMutation(
    api.mailingLists.destroyRecipient.mutationOptions({
      meta: {
        invalidates: invalidatesMailingLists,
        errorMessage: 'Impossible de supprimer le destinataire.',
      },
    }),
  )

  const [addDialogOpen, setAddDialogOpen] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<MailingListRecipientFormValues>({
    resolver: zodResolver(mailingListRecipientFormSchema),
  })

  const handleAddDialogOpenChange = (open: boolean) => {
    setAddDialogOpen(open)
    if (!open) reset()
  }

  const onAddSubmit = (values: MailingListRecipientFormValues) => {
    const name = values.name?.trim() || undefined
    addRecipient.mutate(
      { params: { key }, body: { email: values.email, name } },
      {
        onSuccess: () => {
          toast.success('Destinataire ajouté.')
          setAddDialogOpen(false)
          reset()
        },
      },
    )
  }

  const handleToggleActive = (recipient: MailingListRecipient) => {
    updateRecipient.mutate({ params: { id: recipient.id }, body: { active: !recipient.active } })
  }

  const handleDelete = (id: number) => {
    destroyRecipient.mutate({ params: { id } })
  }

  const columns = [
    columnHelper.accessor('email', {
      header: 'Email',
    }),

    columnHelper.accessor('name', {
      header: 'Nom',
      cell: ({ getValue }) => getValue() ?? '—',
    }),

    columnHelper.accessor('active', {
      header: 'Actif',
      size: 80,
      cell: ({ row }) => {
        const toggle = (
          <Switch
            checked={row.original.active}
            disabled={updateRecipient.isPending || !editGuard.authorized}
            onCheckedChange={() => handleToggleActive(row.original)}
          />
        )

        if (editGuard.authorized) return toggle

        // Un interrupteur désactivé n'émet pas d'évènement pointeur : le déclencheur du tooltip est
        // un `<span>` focusable qui l'enveloppe.
        return (
          <Tooltip>
            <TooltipTrigger render={<span tabIndex={0} />}>{toggle}</TooltipTrigger>
            <TooltipContent>{editGuard.tooltip}</TooltipContent>
          </Tooltip>
        )
      },
    }),

    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      size: 100,
      cell: ({ row }) => (
        <AuthorizedDeleteConfirmButton
          permission={DELETE}
          itemLabel={row.original.email}
          onConfirm={() => handleDelete(row.original.id)}
        />
      ),
    }),
  ]

  const table = useAppTable({
    columns,
    data: mailingList?.recipients ?? [],
  })

  if (!canRead) {
    return <RbacAccessDenied />
  }

  return (
    <>
      <AppHeader>
        <Title>{mailingList?.name ?? '…'}</Title>
      </AppHeader>

      <AppContainer className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">{mailingList?.name ?? '…'}</h2>
            {mailingList?.description && (
              <p className="text-muted-foreground text-sm">{mailingList.description}</p>
            )}
          </div>

          <AuthorizedAppButton permission={ADD} onClick={() => setAddDialogOpen(true)}>
            <Plus className="size-4" />
            Ajouter un destinataire
          </AuthorizedAppButton>
        </div>

        <AppTable>
          <AppTableData table={table} />
        </AppTable>

        {isLoading && <p className="text-muted-foreground text-center text-sm">Chargement…</p>}
      </AppContainer>

      <Dialog open={addDialogOpen} onOpenChange={handleAddDialogOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter un destinataire</DialogTitle>
          </DialogHeader>

          <form id="add-recipient-form" onSubmit={handleSubmit(onAddSubmit)} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="recipient-email">Email *</Label>
              <Input
                id="recipient-email"
                type="email"
                placeholder="exemple@domaine.fr"
                aria-invalid={!!errors.email}
                {...register('email')}
              />
              {errors.email && <p className="text-destructive text-xs">{errors.email.message}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="recipient-name">Nom</Label>
              <Input
                id="recipient-name"
                type="text"
                placeholder="Nom du destinataire (optionnel)"
                aria-invalid={!!errors.name}
                {...register('name')}
              />
              {errors.name && <p className="text-destructive text-xs">{errors.name.message}</p>}
            </div>
          </form>

          <DialogFooter>
            <DialogClose render={<Button variant="outline" />}>Annuler</DialogClose>
            <AuthorizedAppButton
              permission={ADD}
              type="submit"
              form="add-recipient-form"
              disabled={isSubmitting || addRecipient.isPending}
            >
              {addRecipient.isPending ? 'Ajout…' : 'Ajouter'}
            </AuthorizedAppButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
